import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import api from "../lib/api";
import { ArrowLeft, Briefcase, Calendar, User, Tag, CurrencyInr } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGE_COLOR = {
  prospect: "bg-slate-100 text-slate-700 border-slate-300",
  qualification: "bg-blue-100 text-blue-800 border-blue-300",
  proposal: "bg-amber-100 text-amber-800 border-amber-300",
  negotiation: "bg-orange-100 text-orange-800 border-orange-300",
  closed_won: "bg-emerald-100 text-emerald-800 border-emerald-300",
  closed_lost: "bg-rose-100 text-rose-800 border-rose-300",
};

/**
 * Batch C — full-page deal detail used by `Open in New Tab` on deal rows.
 * Uses GET /api/crm/deals/{deal_id} with BDE-scope enforcement.
 */
export default function DealDetailPage({ user, logout }) {
  const { dealId } = useParams();
  const navigate = useNavigate();
  const [deal, setDeal] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true); setError("");
    api.get(`${API}/crm/deals/${dealId}`, {
      params: {
        requester_role: user?.role || "",
        requester_user_id: user?.user_id || "",
        requester_name: user?.name || "",
      },
    }).then((r) => { if (alive) setDeal(r.data); })
      .catch((e) => {
        if (!alive) return;
        const status = e?.response?.status;
        if (status === 403) setError("You do not have access to this deal.");
        else if (status === 404) setError("Deal not found.");
        else setError(e?.response?.data?.detail || "Failed to load deal.");
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [dealId, user?.role, user?.user_id, user?.name]);

  const close = () => navigate("/crm/deals");

  return (
    <div className="min-h-screen bg-slate-50" data-testid="deal-detail-page">
      <div className="border-b border-slate-200 bg-white px-6 py-3 flex items-center gap-3 sticky top-0 z-10">
        <button onClick={close} className="inline-flex items-center gap-1 text-sm text-slate-700 hover:text-[#002FA7]" data-testid="deal-detail-back">
          <ArrowLeft size={16} weight="bold" /> Back to Deals
        </button>
        <div className="text-xs text-slate-400">/ Deal / {dealId.slice(0, 8)}</div>
      </div>
      {loading && <div className="p-10 text-center text-slate-500" data-testid="deal-detail-loading">Loading…</div>}
      {error && (
        <div className="p-10 text-center" data-testid="deal-detail-error">
          <div className="text-rose-700 font-bold mb-2">{error}</div>
          <button onClick={close} className="text-sm text-[#002FA7] underline">← Back to Deals</button>
        </div>
      )}
      {deal && !error && (
        <div className="max-w-3xl mx-auto p-6 space-y-4">
          <div className="bg-white border border-slate-200 p-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500">{deal.deal_id}</div>
                <h1 className="text-3xl font-bold mt-1 flex items-center gap-2"><Briefcase size={24} className="text-[#002FA7]" />{deal.title || "—"}</h1>
                {deal.contact_name && <div className="text-sm text-slate-600 mt-1">Contact: {deal.contact_name} {deal.contact_email ? `· ${deal.contact_email}` : ""}</div>}
              </div>
              <span className={`px-3 py-1 text-[11px] uppercase tracking-wider font-bold border ${STAGE_COLOR[deal.stage] || "border-slate-300 text-slate-700"}`} data-testid="deal-stage-badge">
                {deal.stage || "—"}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Card icon={<CurrencyInr size={16} />} label="Deal Value" value={deal.value ? `₹${Number(deal.value).toLocaleString("en-IN")}` : "—"} testId="deal-value" />
            <Card icon={<Tag size={16} />} label="Probability" value={deal.probability != null ? `${deal.probability}%` : "—"} />
            <Card icon={<User size={16} />} label="Owner" value={deal.assigned_to || deal.lead_owner_name || "—"} testId="deal-owner" />
            <Card icon={<Calendar size={16} />} label="Expected Close" value={(deal.expected_close || "—").toString().slice(0, 10)} />
          </div>

          {deal.source_lead_id && (
            <div className="bg-white border border-slate-200 p-4 text-sm">
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Source Lead</div>
              <a href={`/leads/${deal.source_lead_id}`} target="_blank" rel="noopener noreferrer" className="text-[#002FA7] underline" data-testid="deal-source-lead-link">
                {deal.source_lead_name || deal.source_lead_id}
              </a>
            </div>
          )}

          {deal.notes && (
            <div className="bg-white border border-slate-200 p-4">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Notes</div>
              <div className="text-sm text-slate-700 whitespace-pre-wrap">{deal.notes}</div>
            </div>
          )}

          <div className="text-[11px] text-slate-400">
            Created: {(deal.created_at || "").slice(0, 19).replace("T", " ")}
            {deal.updated_at && <> · Updated: {(deal.updated_at || "").slice(0, 19).replace("T", " ")}</>}
          </div>
        </div>
      )}
    </div>
  );
}

function Card({ icon, label, value, testId }) {
  return (
    <div className="bg-white border border-slate-200 p-3" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 inline-flex items-center gap-1">{icon}{label}</div>
      <div className="text-base font-bold text-slate-800 mt-0.5">{value}</div>
    </div>
  );
}
